import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Sparkle, PaperPlaneTilt, Copy, Check } from "@phosphor-icons/react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { PLATFORMS } from "../lib/meta";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Explainer, Jargon } from "../components/friendly";

export default function SeoWriter() {
  const { activeClientId, activeClient, refreshOrg } = useApp();
  const [keyword, setKeyword] = useState("");
  const [brief, setBrief] = useState("");
  const [articles, setArticles] = useState([]);
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [added, setAdded] = useState({});
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    if (activeClientId) api.get(`/seo-articles?client_id=${activeClientId}`).then((r) => setArticles(r.data));
  }, [activeClientId]);
  useEffect(() => { load(); setCurrent(null); setAdded({}); }, [load]);

  const generate = async () => {
    if (!keyword.trim()) return toast.error("Enter a target keyword");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/seo-article", { client_id: activeClientId, keyword, brief });
      setCurrent(data.article); setAdded({});
      refreshOrg(); load();
      toast.success("Article generated (10 credits)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally { setBusy(false); }
  };

  const sendToDrafts = async (post, i) => {
    try {
      await api.post("/drafts", { client_id: activeClientId, platform: post.platform, caption: post.caption, ai_generated: true });
      setAdded((prev) => ({ ...prev, [i]: true }));
      toast.success("Added to drafts");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add to drafts");
    }
  };

  const copyArticle = () => {
    navigator.clipboard.writeText(`# ${current.title}\n\n${current.article}`);
    setCopied(true);
    toast.success("Article copied as Markdown");
    setTimeout(() => setCopied(false), 2000);
  };

  const renderArticle = (md) => (md || "").split("\n").map((line, i) => {
    if (line.startsWith("## ")) return <h2 key={i}>{line.slice(3)}</h2>;
    if (line.startsWith("# ")) return <h2 key={i}>{line.slice(2)}</h2>;
    if (!line.trim()) return null;
    return <p key={i}>{line.replace(/\*\*/g, "")}</p>;
  });

  return (
    <div className="space-y-6 fade-up max-w-4xl" data-testid="seo-page">
      <div>
        <h1 className="font-display font-bold text-3xl tracking-tight">SEO Article Writer</h1>
        <p className="text-sm text-slate-500 mt-1">Keyword-driven long-form content for <b>{activeClient?.name}</b>, <Jargon tip="Atomizing = breaking one long article into several short social posts, each adapted to its platform.">atomized</Jargon> into social posts. ≈10 credits.</p>
      </div>
      <Explainer id="seo">Give it one keyword you want to rank for. It writes a full SEO article for your website, then atomizes it — turning the article into ready-made social posts you can send to drafts.</Explainer>

      <div className="bg-white border border-slate-200/80 rounded-xl p-6 card-float space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <Input data-testid="seo-keyword-input" value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="Target keyword, e.g. 'light roast brewing guide'" />
          <Button data-testid="seo-generate-button" onClick={generate} disabled={busy} className="rounded-lg gap-2 bg-[hsl(9,69%,62%)] hover:bg-[hsl(8,54%,54%)] shrink-0">
            <Sparkle size={15} weight="fill" /> {busy ? "Writing… (~30s)" : "Generate (10 credits)"}
          </Button>
        </div>
        <Textarea data-testid="seo-brief-input" rows={2} value={brief} onChange={(e) => setBrief(e.target.value)} placeholder="Optional angle or outline notes…" />
      </div>

      {current && (
        <div className="bg-white border border-slate-200/80 rounded-xl p-6 card-float" data-testid="seo-article-view">
          <div className="flex items-start justify-between gap-4">
            <h2 className="font-display font-bold text-2xl tracking-tight">{current.title}</h2>
            <Button data-testid="seo-copy-article-button" size="sm" variant="outline" className="rounded-lg gap-1.5 shrink-0" onClick={copyArticle}>
              {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy article"}
            </Button>
          </div>
          <p className="text-[12px] text-slate-400 mt-1 italic">{current.meta_description}</p>
          <div className="prose-article mt-4 text-sm">{renderArticle(current.article)}</div>
          {current.social_posts?.length > 0 && (
            <div className="mt-6 border-t border-slate-100 pt-5">
              <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-medium mb-3">Atomized social posts</p>
              <div className="space-y-3">
                {current.social_posts.map((p, i) => (
                  <div key={i} className="bg-slate-50 border border-slate-100 rounded-lg p-4 flex gap-3 items-start">
                    <div className="flex-1">
                      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{PLATFORMS[p.platform]?.label || p.platform}</p>
                      <p className="text-[13px] text-slate-700 mt-1 whitespace-pre-wrap">{p.caption}</p>
                    </div>
                    <Button data-testid={`seo-post-to-draft-${i}`} size="sm" variant="outline" disabled={added[i]} className="rounded-lg gap-1.5 shrink-0" onClick={() => sendToDrafts(p, i)}>
                      {added[i] ? <Check size={13} /> : <PaperPlaneTilt size={13} />} {added[i] ? "Added" : "To drafts"}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {articles.length > 0 && (
        <div className="bg-white border border-slate-200/80 rounded-xl card-float divide-y divide-slate-100">
          <div className="px-5 py-4"><h2 className="font-display font-bold text-base">Past articles</h2></div>
          {articles.map((a) => (
            <button key={a.article_id} data-testid={`seo-article-row-${a.article_id}`} onClick={() => { setCurrent(a); setAdded({}); }}
              className="w-full text-left px-5 py-3 hover:bg-slate-50/70 transition-colors">
              <p className="text-[13px] font-medium">{a.title}</p>
              <p className="text-[11px] text-slate-400">Keyword: {a.keyword} · {new Date(a.created_at).toLocaleDateString()}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
